/**
 * Helpers for the `exp_date` the admin API exposes on a StoredPaymentMethod.
 *
 * Wire format is MMYY (4 digits) or null; the backend normalises whatever
 * Payabli returned, so anything else here is treated as "unknown" rather than
 * guessed at. `exp_date` may also be missing entirely from older payloads.
 */

export interface ParsedExpDate {
  month: number;
  year: number;
}

const MMYY = /^(0[1-9]|1[0-2])(\d{2})$/;

export const parseExpDate = (
  raw: string | null | undefined
): ParsedExpDate | null => {
  const match = MMYY.exec(raw ?? "");
  if (!match) return null;
  return { month: Number(match[1]), year: 2000 + Number(match[2]) };
};

export const formatExpDate = (
  raw: string | null | undefined
): string | null => {
  const parsed = parseExpDate(raw);
  if (!parsed) return null;
  return `${String(parsed.month).padStart(2, "0")}/${String(parsed.year % 100).padStart(2, "0")}`;
};

/**
 * A card is valid through the last day of its expiry month, so it is expired
 * once the browser-local clock reaches the first day of the following month.
 * Unparseable input is not expired: we never grey out a card we know nothing about.
 */
export const isExpDateExpired = (
  raw: string | null | undefined,
  now: Date = new Date()
): boolean => {
  const parsed = parseExpDate(raw);
  if (!parsed) return false;
  // `month` is 1-based, so this Date is the first day of the following month.
  return now >= new Date(parsed.year, parsed.month, 1);
};
